import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  User,
  Mail,
  Phone,
  Globe,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Loader,
  Users,
  Briefcase
} from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050';

interface SignupForm {
  // Organization
  organization_name: string;
  preferred_subdomain: string;
  
  // Contact
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  
  // Business
  company_size: string;
  industry: string;
  
  // Plan
  requested_plan: 'basic' | 'professional' | 'enterprise';
  estimated_users: number;
  
  // Billing
  billing_email: string;
  payment_method: string;
  
  // Additional
  use_case: string;
  referral_source: string;
  
  // Admin account
  admin_password: string;
  confirm_password: string;
}

const PLANS = {
  basic: {
    name: 'Basic',
    price: '$49/month',
    features: [
      'Up to 5 users',
      '10 projects',
      '1 GB storage',
      'Email support',
      'Basic analytics'
    ],
    color: 'blue'
  },
  professional: {
    name: 'Professional',
    price: '$199/month',
    features: [
      'Up to 50 users',
      '100 projects',
      '10 GB storage',
      'Priority support',
      'Advanced analytics',
      'API access'
    ],
    color: 'purple'
  },
  enterprise: {
    name: 'Enterprise',
    price: 'Custom',
    features: [
      'Unlimited users',
      'Unlimited projects',
      '100 GB+ storage',
      'Dedicated support',
      'Custom integrations',
      'White labeling',
      'SSO',
      'Audit logs'
    ],
    color: 'gold'
  }
};

const TenantSignup: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [subdomainAvailable, setSubdomainAvailable] = useState<boolean | null>(null);
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  
  const [form, setForm] = useState<SignupForm>({
    organization_name: '',
    preferred_subdomain: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    company_size: '1-10',
    industry: '',
    requested_plan: 'basic',
    estimated_users: 5,
    billing_email: '',
    payment_method: 'credit_card',
    use_case: '',
    referral_source: '',
    admin_password: '',
    confirm_password: ''
  });

  // Check subdomain availability
  useEffect(() => {
    if (form.preferred_subdomain && form.preferred_subdomain.length > 2) {
      const timer = setTimeout(() => {
        checkSubdomain(form.preferred_subdomain);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [form.preferred_subdomain]);

  const checkSubdomain = async (subdomain: string) => {
    setCheckingSubdomain(true);
    try {
      const response = await axios.get(
        `${API_BASE}/api/tenant-signup/check-subdomain/${subdomain}`
      );
      setSubdomainAvailable(response.data.available);
    } catch (error) {
      console.error('Error checking subdomain:', error);
    } finally {
      setCheckingSubdomain(false);
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!form.organization_name) newErrors.organization_name = 'Organization name is required';
      if (!form.preferred_subdomain) newErrors.preferred_subdomain = 'Subdomain is required';
      if (subdomainAvailable === false) newErrors.preferred_subdomain = 'Subdomain is not available';
      if (!form.company_size) newErrors.company_size = 'Company size is required';
    }

    if (step === 2) {
      if (!form.contact_name) newErrors.contact_name = 'Contact name is required';
      if (!form.contact_email) newErrors.contact_email = 'Contact email is required';
      if (form.contact_email && !/\S+@\S+\.\S+/.test(form.contact_email)) {
        newErrors.contact_email = 'Invalid email format';
      }
    }

    if (step === 3) {
      if (!form.requested_plan) newErrors.requested_plan = 'Please select a plan';
    }

    if (step === 4) {
      if (!form.admin_password) newErrors.admin_password = 'Password is required';
      if (form.admin_password && form.admin_password.length < 8) {
        newErrors.admin_password = 'Password must be at least 8 characters';
      }
      if (form.admin_password !== form.confirm_password) {
        newErrors.confirm_password = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;

    setLoading(true);
    setErrors({});

    try {
      const response = await axios.post(`${API_BASE}/api/tenant-signup`, {
        ...form,
        billing_email: form.billing_email || form.contact_email
      });

      setSuccess(true);
    } catch (error: any) {
      console.error('Signup error:', error);
      setErrors({
        general: error.response?.data?.error || 'Failed to submit request'
      });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Request Submitted Successfully!
          </h2>
          
          <p className="text-gray-600 mb-6">
            Thank you for your interest! Your tenant request has been submitted for review.
          </p>
          
          <div className="bg-blue-50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold text-blue-900 mb-2">Next Steps:</h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li>• Our team will review your request within 24 hours</li>
              <li>• You'll receive an email once your tenant is approved</li>
              <li>• After approval, access your tenant at:</li>
              <li className="font-mono bg-white px-2 py-1 rounded">
                https://{form.preferred_subdomain}.process.env.FRONTEND_URL
              </li>
            </ul>
          </div>
          
          <button
            onClick={() => navigate('/')}
            className="w-full bg-dusky-purple text-white py-2 px-4 rounded-lg hover:bg-blue-600"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Start Your Free Trial
          </h1>
          <p className="text-gray-600">
            Get started with JARVIS RCSA Platform in minutes
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4].map((step) => (
              <div
                key={step}
                className={`flex-1 ${step < 4 ? 'mr-2' : ''}`}
              >
                <div
                  className={`h-2 rounded-full ${
                    step <= currentStep ? 'bg-dusky-purple' : 'bg-gray-200'
                  }`}
                />
                <p className="text-xs text-gray-600 mt-2">
                  {step === 1 && 'Organization'}
                  {step === 2 && 'Contact'}
                  {step === 3 && 'Plan'}
                  {step === 4 && 'Account'}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          {errors.general && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              {errors.general}
            </div>
          )}

          {/* Step 1: Organization Info */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Organization Information
              </h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization Name *
                </label>
                <input
                  type="text"
                  value={form.organization_name}
                  onChange={(e) => setForm({ ...form, organization_name: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-dusky-purple focus:border-transparent ${
                    errors.organization_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Acme Corporation"
                />
                {errors.organization_name && (
                  <p className="text-red-500 text-sm mt-1">{errors.organization_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preferred Subdomain *
                </label>
                <div className="flex items-center">
                  <input
                    type="text"
                    value={form.preferred_subdomain}
                    onChange={(e) => setForm({ ...form, preferred_subdomain: e.target.value.toLowerCase() })}
                    className={`flex-1 px-3 py-2 border rounded-l-lg focus:ring-2 focus:ring-dusky-purple focus:border-transparent ${
                      errors.preferred_subdomain ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="acme"
                  />
                  <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg text-gray-600">
                    .process.env.FRONTEND_URL
                  </span>
                </div>
                {checkingSubdomain && (
                  <p className="text-gray-500 text-sm mt-1 flex items-center">
                    <Loader className="w-4 h-4 mr-1 animate-spin" />
                    Checking availability...
                  </p>
                )}
                {!checkingSubdomain && subdomainAvailable === true && form.preferred_subdomain && (
                  <p className="text-green-500 text-sm mt-1 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Subdomain is available
                  </p>
                )}
                {!checkingSubdomain && subdomainAvailable === false && (
                  <p className="text-red-500 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    Subdomain is not available
                  </p>
                )}
                {errors.preferred_subdomain && (
                  <p className="text-red-500 text-sm mt-1">{errors.preferred_subdomain}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Size *
                </label>
                <select
                  value={form.company_size}
                  onChange={(e) => setForm({ ...form, company_size: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusky-purple focus:border-transparent"
                >
                  <option value="1-10">1-10 employees</option>
                  <option value="11-50">11-50 employees</option>
                  <option value="51-200">51-200 employees</option>
                  <option value="201-500">201-500 employees</option>
                  <option value="500+">500+ employees</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Industry
                </label>
                <input
                  type="text"
                  value={form.industry}
                  onChange={(e) => setForm({ ...form, industry: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusky-purple focus:border-transparent"
                  placeholder="Financial Services"
                />
              </div>
            </div>
          )}

          {/* Step 2: Contact Info */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Contact Information
              </h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Name *
                </label>
                <input
                  type="text"
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-dusky-purple focus:border-transparent ${
                    errors.contact_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="John Doe"
                />
                {errors.contact_name && (
                  <p className="text-red-500 text-sm mt-1">{errors.contact_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Email *
                </label>
                <input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-dusky-purple focus:border-transparent ${
                    errors.contact_email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="john@acme.com"
                />
                {errors.contact_email && (
                  <p className="text-red-500 text-sm mt-1">{errors.contact_email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={form.contact_phone}
                  onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusky-purple focus:border-transparent"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Use Case
                </label>
                <textarea
                  value={form.use_case}
                  onChange={(e) => setForm({ ...form, use_case: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusky-purple focus:border-transparent"
                  placeholder="Tell us how you plan to use JARVIS RCSA..."
                />
              </div>
            </div>
          )}

          {/* Step 3: Plan Selection */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Choose Your Plan
              </h2>
              
              <div className="grid gap-4">
                {Object.entries(PLANS).map(([key, plan]) => (
                  <div
                    key={key}
                    onClick={() => setForm({ ...form, requested_plan: key as any })}
                    className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                      form.requested_plan === key
                        ? 'border-electric-blue bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {plan.name}
                        </h3>
                        <p className="text-2xl font-bold text-dusky-purple mt-1">
                          {plan.price}
                        </p>
                      </div>
                      {form.requested_plan === key && (
                        <CheckCircle className="w-6 h-6 text-dusky-purple" />
                      )}
                    </div>
                    <ul className="space-y-2">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estimated Number of Users
                </label>
                <input
                  type="number"
                  value={form.estimated_users}
                  onChange={(e) => setForm({ ...form, estimated_users: parseInt(e.target.value) || 5 })}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusky-purple focus:border-transparent"
                />
              </div>
            </div>
          )}

          {/* Step 4: Account Setup */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Set Up Your Admin Account
              </h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Admin Password *
                </label>
                <input
                  type="password"
                  value={form.admin_password}
                  onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-dusky-purple focus:border-transparent ${
                    errors.admin_password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Minimum 8 characters"
                />
                {errors.admin_password && (
                  <p className="text-red-500 text-sm mt-1">{errors.admin_password}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password *
                </label>
                <input
                  type="password"
                  value={form.confirm_password}
                  onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-dusky-purple focus:border-transparent ${
                    errors.confirm_password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Re-enter your password"
                />
                {errors.confirm_password && (
                  <p className="text-red-500 text-sm mt-1">{errors.confirm_password}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Billing Email
                </label>
                <input
                  type="email"
                  value={form.billing_email}
                  onChange={(e) => setForm({ ...form, billing_email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusky-purple focus:border-transparent"
                  placeholder="billing@acme.com (optional)"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Leave blank to use contact email for billing
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  How did you hear about us?
                </label>
                <input
                  type="text"
                  value={form.referral_source}
                  onChange={(e) => setForm({ ...form, referral_source: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusky-purple focus:border-transparent"
                  placeholder="Google, LinkedIn, etc."
                />
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={`px-4 py-2 rounded-lg ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Previous
            </button>

            {currentStep < 4 ? (
              <button
                onClick={handleNext}
                className="px-6 py-2 bg-dusky-purple text-white rounded-lg hover:bg-blue-600"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-6 py-2 bg-dusky-purple text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {loading ? (
                  <>
                    <Loader className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-600">
          Already have an account?{' '}
          <button
            onClick={() => navigate('/login')}
            className="text-dusky-purple hover:underline"
          >
            Sign in
          </button>
        </div>
      </div>
    </div>
  );
};

export default TenantSignup;